/**
 * One slot's ranked order on the scenario pitch (SE-8393, PV23 · D9).
 *
 * The backend persists a global rank per occupant (`ScenarioPitch.entries`);
 * the per-slot rank is the ordinal among the slot's occupants. An occupant
 * nobody ranked yet — the pitch derived them, the first write has not
 * happened — sorts after every ranked one, by GPR desc (§4.1 merge rule).
 * Pending reorders replay over that base exactly as the watchlist queue
 * replays them, so the pop-out and the card agree while a save is in flight.
 * @module features/squad-planning/utils/scenarioSlotOrder
 */
import {
  applyRelativePreferenceOperation,
  isPitchAssignmentOperation,
  isPitchFormationOperation,
  type WatchlistPitchOperation,
} from "@/features/scouting/hooks/watchlistPreferenceOrderState";

/** The two facts the ordering reads off an occupant. */
export interface SlotOccupant {
  readonly playerId: string;
  readonly gpr: number | null;
}

/** What the pitch projection needs to rank every slot. */
export interface ScenarioSlotOrderSource {
  /** Persisted global order — `entries` by rank. */
  readonly orderedPlayerIds: readonly string[];
  /** Reorders not yet confirmed by the server, oldest first. */
  readonly operations: readonly WatchlistPitchOperation[];
}

/**
 * Ranked occupants first, in persisted order; the rest by GPR desc, ties
 * keeping the input order.
 * @param occupants - The slot's occupants in derived order
 * @param rankedPlayerIds - Persisted global order
 * @returns The occupants in slot order
 */
export const orderSlotOccupants = <Occupant extends SlotOccupant>(
  occupants: readonly Occupant[],
  rankedPlayerIds: readonly string[]
): readonly Occupant[] => {
  const rank = new Map(rankedPlayerIds.map((id, index) => [id, index]));
  return occupants
    .map((occupant, index) => ({ occupant, index }))
    .sort((left, right) => {
      const leftRank = rank.get(left.occupant.playerId);
      const rightRank = rank.get(right.occupant.playerId);
      if (leftRank !== undefined && rightRank !== undefined) {
        return leftRank - rightRank;
      }
      if (leftRank !== undefined) return -1;
      if (rightRank !== undefined) return 1;
      const byGpr =
        (right.occupant.gpr ?? Number.NEGATIVE_INFINITY) -
        (left.occupant.gpr ?? Number.NEGATIVE_INFINITY);
      return byGpr || left.index - right.index;
    })
    .map(({ occupant }) => occupant);
};

/**
 * Replays the pending reorders over one slot's base order. Assignment and
 * formation operations are not this leaf's and are skipped.
 * @param baseOrder - The slot's occupants in persisted-then-GPR order
 * @param operations - Pending operations, oldest first
 * @returns The projected slot order
 */
export const projectSlotOrder = (
  baseOrder: readonly string[],
  operations: readonly WatchlistPitchOperation[]
): readonly string[] =>
  operations.reduce<readonly string[]>(
    (order, operation) =>
      isPitchAssignmentOperation(operation) ||
      isPitchFormationOperation(operation)
        ? order
        : applyRelativePreferenceOperation(order, operation),
    baseOrder
  );

/**
 * The slot's full projected order for a set of occupants.
 * @param occupants - The slot's occupants in derived order
 * @param source - Persisted order and pending operations
 * @returns Player ids in slot order
 */
export const projectSlotOccupantIds = (
  occupants: readonly SlotOccupant[],
  source: ScenarioSlotOrderSource
): readonly string[] =>
  projectSlotOrder(
    orderSlotOccupants(occupants, source.orderedPlayerIds).map(
      occupant => occupant.playerId
    ),
    source.operations
  );

/**
 * The Added-players list in persisted order (SE-8393); unchanged on Current
 * Squad, where no order is read.
 * @param added - Added players in derived order
 * @param source - Persisted order and pending operations, when read
 * @returns The same players in slot order
 */
export const orderAddedPlayers = <
  Player extends { readonly id: string; readonly gprScore: number | null },
>(
  added: readonly Player[],
  source: ScenarioSlotOrderSource | undefined
): readonly Player[] => {
  if (!source) return added;
  const byId = new Map(added.map(player => [player.id, player]));
  return projectSlotOccupantIds(
    added.map(player => ({ playerId: player.id, gpr: player.gprScore })),
    source
  ).flatMap(id => {
    const player = byId.get(id);
    return player ? [player] : [];
  });
};

/**
 * Sorts each group of a list in place — the members of one group take the
 * positions that group already held, so the other groups never move. Items
 * with no group are left where they are.
 * @param items - The list
 * @param groupOf - The group key of one item, or null for none
 * @param sortGroup - Sorts one group's members
 * @returns The list with every group sorted in place
 */
export const reorderWithinGroups = <Item, Key>(
  items: readonly Item[],
  groupOf: (item: Item) => Key | null,
  sortGroup: (key: Key, members: readonly Item[]) => readonly Item[]
): readonly Item[] => {
  const keys = items.map(groupOf);
  const groups = [...new Set(keys.filter(key => key !== null))] as Key[];
  const sortedByGroup = new Map(
    groups.map(key => [
      key,
      sortGroup(
        key,
        items.filter((_, index) => keys[index] === key)
      ),
    ])
  );
  // An item's place in its group = how many of that group came before it.
  return items.map((item, index) => {
    const key = keys[index];
    if (key === null) return item;
    const ordinal = keys.slice(0, index).filter(other => other === key).length;
    return sortedByGroup.get(key)?.[ordinal] ?? item;
  });
};
